import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useTenant } from '@/contexts/TenantContext';
import {
  fetchManagementAccess,
  holderLabel,
  type ManagementAccess,
} from '@/lib/managementAccess';

/**
 * SUB-3.14 · FR60. "A client can see who has access to their account."
 *
 * ⚠️ THE WORDING IS THE REQUIREMENT, NOT DECORATION. This states that an
 * organization HOLDS MANAGEMENT ACCESS to this account. It must never say that
 * this account is "a sub-account of" anything, or mention a parent, a hierarchy,
 * siblings, or how many other accounts that organization manages. An account may
 * know who has access to it; it may not learn that a hierarchy exists (ISO1 /
 * SUB-2.12 / FR19 against FR60, both intended, epics.md:1308-1310).
 *
 * ⚠️ RENDERS NOTHING, NOT AN EMPTY SECTION, when nobody outside holds access
 * (AC6). `account_management_access()` returns NULL rather than an empty object
 * for exactly that, so an ordinary standalone account is visually identical to
 * how it was before this story.
 *
 * ⚠️ RE-FETCHES RATHER THAN CACHES (AC5, and SUB-3.9's AC4 depends on it). After
 * a detach the account has no parent, the notice must disappear on the next open,
 * and the effect below refires on both the account id and its parent id.
 */
export const ManagementAccessNotice = () => {
  const { currentTenant } = useTenant();
  const [access, setAccess] = useState<ManagementAccess | null>(null);

  const tenantId = currentTenant?.tenant_id;
  const parentId = currentTenant?.parent_tenant_id ?? null;

  useEffect(() => {
    let cancelled = false;

    // Deliberately NOT gated on `parentId` being set. That value comes from a
    // browser read of `tenants`, and the server answer is the one that decides.
    // The id is a refetch trigger, never the condition.
    fetchManagementAccess().then((result) => {
      if (!cancelled) setAccess(result);
    });

    return () => {
      cancelled = true;
    };
  }, [tenantId, parentId]);

  if (!access) return null;

  const organization = access.parentCompanyName ?? 'An external organization';
  const grantedOn = access.grantedAt ? format(new Date(access.grantedAt), 'MMM d, yyyy') : null;

  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div className="space-y-3 min-w-0">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Management access</h3>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{organization}</span> holds
                management access to this account
                {grantedOn ? `, first granted ${grantedOn}` : ''}. The people below can work in
                this account as administrators. Only a Joules superadmin can remove that access.
              </p>
            </div>

            <ul className="space-y-1">
              {access.holders.map((holder) => (
                <li key={holder.userId} className="text-sm text-foreground">
                  <span className="font-medium">{holderLabel(holder)}</span>
                  {holder.email && holder.name ? (
                    <span className="text-muted-foreground"> · {holder.email}</span>
                  ) : null}
                  {holder.grantedAt ? (
                    <span className="text-muted-foreground">
                      {' '}
                      · access granted {format(new Date(holder.grantedAt), 'MMM d, yyyy')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ManagementAccessNotice;
